/*
 * Design: Swiss Precision × 和の余白（構造的ミニマリズム）
 * Brand: eightone (#E03030 accent, #1A1A1A headings, #333333 body, Noto Sans JP)
 * Layout: Asymmetric left-aligned headings, generous whitespace
 *
 * Section Flow (Background alternation: W=white, G=#F9F9F9, D=dark):
 * 1. Hero — 価値提案 (W)
 * 2. Stats — 数値インパクト (G)
 * 3. Problem — 一般的な業務課題 (W)
 * 4. SaaS Challenge — SaaS導入の壁 (G)
 * 5. Technology — Antigravity × MCP × Gemini (W)
 * 6. TrustBadges — パートナーロゴマーキー (G)
 * 7. Partner — 東洋電設連携 (W border)
 * 8. Industry Solutions — 4業種別ソリューション (G)
 * 9. Services — 3つのサービスメニュー (W)
 * 9.5. Solution Items & Sets (G)
 * 9.7. ROI Simulator (W)
 * 10. Case Studies (G)
 * 11. Steps (W)
 * 12. Subsidy (D)
 * 12.5. Seminar & Training — DXセミナー・研修 (G) ← AEO新規
 * 13. Note Articles (G)
 * 14. Identity Block — LLM引用向け (W border) ← AEO新規
 * 14.5. Company (W)
 * 15. FAQ (G)
 * 16. Contact Form (G)
 * 17. CTA (W)
 *
 * AEO対策:
 * - SeminarSection: 中四国DX・AIセミナー・研修の地域特化セクション
 * - IdentityBlock: LLMが引用しやすい構造化された会社紹介
 * - FAQSection: 中四国セミナー関連FAQ追加
 * - JSON-LD: Organization, Course, Event, FAQPage, Speakable
 * - llms.txt / llms-full.txt: AIクローラー向け情報提供
 */

import { useEffect } from "react";
import { Header } from "@/components/Header";
import { SkipNav } from "@/components/SkipNav";
import { ScrollProgress } from "@/components/ScrollProgress";
import { HeroSection } from "@/components/HeroSection";
import { StatsSection } from "@/components/StatsSection";
import { ProblemSection } from "@/components/ProblemSection";
import { SaaSChallengeSection } from "@/components/SaaSChallengeSection";
import { TechnologySection } from "@/components/TechnologySection";
import { TrustBadgesSection } from "@/components/TrustBadgesSection";
import { PartnerSection } from "@/components/PartnerSection";
import { IndustrySolutionsSection } from "@/components/IndustrySolutionsSection";
import { ServicesSection } from "@/components/ServicesSection";
import { CaseStudySection } from "@/components/CaseStudySection";
import { StepsSection } from "@/components/StepsSection";
import { SubsidySection } from "@/components/SubsidySection";
import { SeminarSection } from "@/components/SeminarSection";
import { NoteArticlesSection } from "@/components/NoteArticlesSection";
import { IdentityBlock } from "@/components/IdentityBlock";
import { CompanySection } from "@/components/CompanySection";
import { FAQSection } from "@/components/FAQSection";
import { CTASection } from "@/components/CTASection";
import { Footer } from "@/components/Footer";
import { ScrollToTop } from "@/components/ScrollToTop";
import { FloatingCTA } from "@/components/FloatingCTA";
import { SectionDivider } from "@/components/SectionDivider";
import { SolutionItemsSection } from "@/components/SolutionItemsSection";
import { SimulatorSection } from "@/components/SimulatorSection";
import { ContactFormSection } from "@/components/ContactFormSection";

export default function Home() {
  useEffect(() => {
    document.title = "AI業務自動化・DXセミナー・研修 | 広島・中四国対応 | 81合同会社";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white scroll-smooth">
      <SkipNav />
      <Header />
      <ScrollProgress />
      <main id="main-content" role="main">
        {/* 1. Hero (W) */}
        <HeroSection />

        {/* 2. Stats (G) */}
        <StatsSection />

        {/* Divider: G → W */}
        <SectionDivider variant="wave" fromColor="#F9F9F9" toColor="#FFFFFF" />

        {/* 3. Problem (W) */}
        <ProblemSection />

        {/* Divider: W → G */}
        <SectionDivider variant="curve" fromColor="#FFFFFF" toColor="#F9F9F9" />

        {/* 4. SaaS Challenge (G) */}
        <SaaSChallengeSection />

        {/* Divider: G → W */}
        <SectionDivider variant="wave" fromColor="#F9F9F9" toColor="#FFFFFF" />

        {/* 5. Technology (W) */}
        <TechnologySection />

        {/* 6. TrustBadges (G) */}
        <TrustBadgesSection />

        {/* 7. Partner (W border) */}
        <PartnerSection />

        {/* Divider: W → G */}
        <SectionDivider variant="angle" fromColor="#FFFFFF" toColor="#F9F9F9" />

        {/* 8. Industry Solutions (G) */}
        <IndustrySolutionsSection />

        {/* Divider: G → W */}
        <SectionDivider variant="wave" fromColor="#F9F9F9" toColor="#FFFFFF" />

        {/* 9. Services (W) */}
        <ServicesSection />

        {/* Divider: W → G */}
        <SectionDivider variant="curve" fromColor="#FFFFFF" toColor="#F9F9F9" />

        {/* 9.5. Solution Items & Sets (G) */}
        <SolutionItemsSection />

        {/* Divider: G → W */}
        <SectionDivider variant="wave" fromColor="#F9F9F9" toColor="#FFFFFF" />

        {/* 9.7. ROI Simulator (W) */}
        <SimulatorSection />

        {/* Divider: W → G */}
        <SectionDivider variant="curve" fromColor="#FFFFFF" toColor="#F9F9F9" />

        {/* 10. Case Studies (G) */}
        <CaseStudySection />

        {/* Divider: G → W */}
        <SectionDivider variant="wave" fromColor="#F9F9F9" toColor="#FFFFFF" />

        {/* 11. Steps (W) */}
        <StepsSection />

        {/* 12. Subsidy (D) */}
        <SubsidySection />

        {/* 12.5. Seminar & Training (G) — AEO: 中四国DX・AIセミナー */}
        <SeminarSection />

        {/* 13. Note Articles (G) */}
        <NoteArticlesSection />

        {/* Divider: G → W */}
        <SectionDivider variant="curve" fromColor="#F9F9F9" toColor="#FFFFFF" />

        {/* 14. Identity Block (W border) — AEO: LLM引用向け */}
        <IdentityBlock />

        {/* 14.5. Company (W) */}
        <CompanySection />

        {/* Divider: W → G */}
        <SectionDivider variant="wave" fromColor="#FFFFFF" toColor="#F9F9F9" />

        {/* 15. FAQ (G) */}
        <FAQSection />

        {/* Divider: G → W */}
        <SectionDivider variant="angle" fromColor="#F9F9F9" toColor="#FFFFFF" flip />

        {/* 16. Contact Form (G) */}
        <ContactFormSection />

        {/* Divider: G → W */}
        <SectionDivider variant="wave" fromColor="#F9F9F9" toColor="#FFFFFF" />

        {/* 17. CTA (W) */}
        <CTASection />
      </main>
      <Footer />
      <ScrollToTop />
      <FloatingCTA />
    </div>
  );
}
